import { IconButton, MoonIcon, SunIcon } from "@carbon6/c6-frontend-design";
import { useEffect, useState } from "react";
import { hatcheryDatabase } from "@/app/hatcheryDatabase";
import { useThemeModePreference } from "@/app/theme";
import { formatBytes } from "@/utils";
import styles from "./GeneralSettingsPanel.module.css";
import { readStorageUsage, type StorageUsage } from "./storageUsage";

// 일반 설정 패널이다. 테마(밝게/어둡게)와 로컬 캐시를 다룬다. 각 행은 라벨·설명(좌) + 컨트롤(우)이다.
export function GeneralSettingsPanel() {
  const [themeMode, setThemeMode] = useThemeModePreference();
  const [usage, setUsage] = useState<StorageUsage>({ status: "loading" });
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    let active = true;
    void readStorageUsage().then((next) => {
      if (active) {
        setUsage(next);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  const handleClear = (): void => {
    setClearing(true);
    void hatcheryDatabase
      .clearAll()
      .then(readStorageUsage)
      .then((next) => {
        setUsage(next);
        setClearing(false);
      });
  };

  return (
    <div className={styles.body}>
      <header className={styles.header}>
        {/* 창 제목("설정", Modal.Header.Title 의 h2) 아래 단계다. */}
        <h3 className={styles.title}>일반 설정</h3>
      </header>
      <section className={styles.section}>
        <div className={styles.row}>
          <div className={styles.text}>
            <p className={styles.label}>테마</p>
            <p className={styles.desc}>이 기기에서 사용할 테마를 선택합니다.</p>
          </div>
          <div className={styles.control}>
            <IconButton
              aria-label="밝은 테마"
              title="밝은 테마를 사용합니다."
              aria-pressed={themeMode === "light"}
              variant={themeMode === "light" ? "solid" : "surface"}
              onClick={() => setThemeMode("light")}
            >
              <SunIcon />
            </IconButton>
            <IconButton
              aria-label="어두운 테마"
              title="어두운 테마를 사용합니다."
              aria-pressed={themeMode === "dark"}
              variant={themeMode === "dark" ? "solid" : "surface"}
              onClick={() => setThemeMode("dark")}
            >
              <MoonIcon />
            </IconButton>
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.text}>
            <p className={styles.label}>로컬 캐시</p>
            <p className={styles.desc}>
              한 번 받은 화면 데이터를 이 기기에 저장해 다시 받지 않습니다. 비우면 다음 조회가 잠시
              느려집니다.
            </p>
            <p className={styles.desc}>{usageText(usage)}</p>
          </div>
          <div className={styles.control}>
            <IconButton
              text="비우기"
              size="md"
              variant="surface"
              disabled={clearing}
              title="이 기기에 저장해 둔 화면 데이터를 지웁니다."
              onClick={handleClear}
            />
          </div>
        </div>
      </section>
    </div>
  );
}

// 사용량은 origin 전체의 근삿값이다(다른 저장소도 함께 센다). 그래서 "이 사이트" 로 적는다.
function usageText(usage: StorageUsage): string {
  if (usage.status === "loading") {
    return "사용량을 확인하고 있습니다.";
  }
  if (usage.status === "unknown") {
    return "사용량은 이 브라우저에서 확인할 수 없습니다.";
  }
  return `이 사이트가 이 기기에서 약 ${formatBytes(usage.bytes)} 를 쓰고 있습니다.`;
}
